import { useCallback, useEffect, useState } from "react";

// Shared state/logic for building a multi-entry facility booking (1-10 dated
// time-slot "entries", all on one facility) before confirming it in a single
// checkout. Used by MemberDashboard.jsx, CoachDashboard.jsx, and
// BookingsManagement.jsx's staff booking tab — the one deliberate exception
// to this codebase's "duplicate independently" UI convention, since the cart
// is a real state machine (add/remove, a same-facility invariant, a
// debounced multi-entry preview, and 409-conflict-to-entry reconciliation),
// not the few lines of pure rendering logic that convention was meant for.
// Each call site still owns its own JSX/markup independently as before.

let cartEntrySeq = 0;
const nextClientKey = () => `cart-entry-${++cartEntrySeq}`;

// Strips UI-only fields (clientKey, facility_id) down to what the backend's
// entries[] payload actually wants.
const toApiEntry = ({ facility_slot_id, booking_date, start_time, end_time }) => ({
  facility_slot_id,
  booking_date,
  start_time,
  end_time,
});

const PREVIEW_DEBOUNCE_MS = 300;

/**
 * @param {object} opts
 * @param {"member"|"staff"} [opts.audience] - only affects which max-horizon
 *   hint (30 vs 90 days) callers should show in copy/validation; the hook
 *   itself doesn't validate dates, that's left to the caller building an
 *   entry and to the backend (authoritative either way).
 * @param {(payload: {facility_id, entries}) => Promise<any>} opts.previewFn -
 *   previewBooking for member carts, previewStaffBooking for staff carts.
 * @param {number} [opts.maxEntries]
 * @param {object|null} [opts.previewExtra] - extra fields merged into every
 *   preview request (the staff cart passes the member_id/member_code/email
 *   there, since POST /api/staff/bookings/preview 422s without a member).
 *   Omit it for carts that need nothing extra; pass null to hold off
 *   previewing until those fields are available.
 */
export function useBookingCart({ audience = "member", previewFn, maxEntries = 10, previewExtra } = {}) {
  const [entries, setEntries] = useState([]);
  const [facilityId, setFacilityIdState] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [entryConflicts, setEntryConflicts] = useState(new Map());

  const canAddMore = entries.length < maxEntries;
  const isFull = !canAddMore;

  // entry: { facility_id, facility_slot_id, slot_code, booking_date,
  // start_time, end_time }. Rejects (without mutating state) if the cart is
  // full, or if it's non-empty and this entry belongs to a different
  // facility — the API can't support a cross-facility booking anyway.
  const addEntry = useCallback(
    (entry) => {
      if (entries.length >= maxEntries) {
        return { ok: false, reason: "max_entries" };
      }
      if (facilityId != null && entry.facility_id != null && entry.facility_id !== facilityId) {
        return { ok: false, reason: "different_facility" };
      }
      const clientKey = nextClientKey();
      setEntries((prev) => [...prev, { ...entry, clientKey }]);
      if (facilityId == null && entry.facility_id != null) {
        setFacilityIdState(entry.facility_id);
      }
      return { ok: true, clientKey };
    },
    [entries.length, facilityId, maxEntries]
  );

  const removeEntry = useCallback((clientKey) => {
    setEntries((prev) => {
      const next = prev.filter((e) => e.clientKey !== clientKey);
      if (next.length === 0) setFacilityIdState(null);
      return next;
    });
    setEntryConflicts((prev) => {
      if (!prev.has(clientKey)) return prev;
      const next = new Map(prev);
      next.delete(clientKey);
      return next;
    });
  }, []);

  const updateEntry = useCallback((clientKey, patch) => {
    setEntries((prev) => prev.map((e) => (e.clientKey === clientKey ? { ...e, ...patch } : e)));
    setEntryConflicts((prev) => {
      if (!prev.has(clientKey)) return prev;
      const next = new Map(prev);
      next.delete(clientKey);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setEntries([]);
    setFacilityIdState(null);
    setPreview(null);
    setPreviewError(null);
    setEntryConflicts(new Map());
  }, []);

  // Explicit facility switch (e.g. browsing a different facility with an
  // empty cart) — kept separate from the implicit set-on-first-entry above
  // so callers can reset the "locked" facility without needing an entry.
  const setFacility = useCallback((id) => {
    setFacilityIdState(id);
  }, []);

  // Maps each conflict's positional entry_index back onto the clientKey of
  // the entry that WAS at that index at submit time (submittedEntries must
  // be the snapshot passed to createBooking/createStaffBooking, not the
  // live `entries` — the live array shouldn't have changed by the time a
  // 409 comes back, but snapshotting keeps the mapping correct regardless).
  const applyConflicts = useCallback((conflicts, submittedEntries) => {
    setEntryConflicts((prev) => {
      const next = new Map(prev);
      (conflicts || []).forEach((conflict) => {
        const submitted = submittedEntries?.[conflict.entry_index];
        if (submitted?.clientKey) {
          next.set(submitted.clientKey, conflict);
        }
      });
      return next;
    });
  }, []);

  // Debounced multi-entry preview — same 300ms pattern already used
  // elsewhere in this codebase (e.g. MemberDashboard.jsx's rangePreview
  // effect, BookingsManagement.jsx's member search).
  const apiEntriesKey = JSON.stringify(entries.map(toApiEntry));
  const previewExtraKey = JSON.stringify(previewExtra ?? null);

  useEffect(() => {
    if (!previewFn || entries.length === 0 || facilityId == null || previewExtra === null) {
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return undefined;
    }
    let on = true;
    setPreviewLoading(true);
    setPreviewError(null);
    const t = setTimeout(() => {
      previewFn({ ...previewExtra, facility_id: facilityId, entries: entries.map(toApiEntry) })
        .then((data) => on && setPreview(data))
        .catch((err) => {
          if (!on) return;
          setPreview(null);
          setPreviewError(err?.response?.data?.message || "Could not calculate the fee for this booking.");
        })
        .finally(() => on && setPreviewLoading(false));
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      on = false;
      clearTimeout(t);
    };
    // apiEntriesKey/previewExtraKey are stable serializations of the fields
    // that actually affect the preview call; entries/previewExtra are
    // covered by them plus the explicit facilityId dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewFn, facilityId, apiEntriesKey, previewExtraKey]);

  return {
    audience,
    maxEntries,
    entries,
    facilityId,
    addEntry,
    removeEntry,
    updateEntry,
    clearCart,
    setFacility,
    preview,
    previewLoading,
    previewError,
    entryConflicts,
    applyConflicts,
    canAddMore,
    isFull,
  };
}

export { toApiEntry };
